import {
  CartDiscountUpdateEvent,
  CartErrorEvent,
  CartLinesUpdateEvent,
  CartViewEvent,
  CollectionUpdateEvent,
  ProductSelectEvent,
  SearchUpdateEvent
} from 'https://cdn.shopify.com/storefront/standard-events.js';

const BRIDGE_SOURCE = 'woodstock-legacy-bridge';
const ACTION_SOURCE = 'woodstock-standard-action';
const selectedVariantByProduct = new WeakMap();

const getCartTarget = () => (
  document.querySelector('cart-drawer')
  || document.querySelector('cart-items')
  || document
);

const getProductTarget = (sectionId) => {
  const productInfo = (
    document.querySelector(`product-info[data-section="${CSS.escape(sectionId || '')}"]`)
    || document.querySelector('product-info')
  );
  const originalSection = productInfo?.dataset.originalSection;

  return (
    originalSection
      ? document.querySelector(`product-info[data-section="${CSS.escape(originalSection)}"]`)
      : productInfo
  ) || productInfo || document;
};

const fetchAjaxCart = async () => {
  const response = await fetch(`${window.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' }
  });
  if (!response.ok) throw new Error(window.cartStrings?.error || 'Unable to retrieve the cart.');
  return response.json();
};

const createStandardCart = (ajaxCart) => CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart);

const normalizeMoney = (price) => {
  const currencyCode = window.presentmentCurrency;
  const { maximumFractionDigits } = new Intl.NumberFormat('en', {
    style: 'currency',
    currency: currencyCode
  }).resolvedOptions();

  return {
    amount: new Intl.NumberFormat('en', {
      style: 'decimal',
      minimumFractionDigits: maximumFractionDigits,
      maximumFractionDigits,
      useGrouping: false
    }).format(Number(price || 0) / 100),
    currencyCode
  };
};

const getOptionName = (group, index) => {
  const selectName = group.querySelector('select[name^="options["]')?.name;
  const selectNameMatch = selectName?.match(/^options\[(.*)]$/);
  if (selectNameMatch) return selectNameMatch[1];

  const label = group.querySelector('.product-form__input__form-label');
  if (!label) return `Option ${index + 1}`;

  const cleanLabel = label.cloneNode(true);
  cleanLabel.querySelector('.product-form__input__option-value')?.remove();
  return cleanLabel.textContent.replace(':', '').trim() || `Option ${index + 1}`;
};

const getSelectedOptions = (productInfo, variant) => {
  const optionNames = [...productInfo.querySelectorAll('fieldset, .product-form__input')]
    .map(getOptionName)
    .filter(Boolean);

  return (variant?.options || []).map((value, index) => ({
    name: optionNames[index] || `Option ${index + 1}`,
    value
  }));
};

const getProductTitle = (productInfo) => productInfo.dataset.standardProductTitle || '';

const getProductHandle = (productInfo) => productInfo.dataset.standardProductHandle || '';

const dispatchCartError = (target, error, code = 'INVALID') => {
  target.dispatchEvent(new CartErrorEvent({
    error: `${error || window.cartStrings?.error || 'Cart update failed.'}`,
    code
  }));
};

const resolveCartEvent = async (deferred, EventClass, target) => {
  try {
    const ajaxCart = await fetchAjaxCart();
    deferred.resolve({
      cart: EventClass.createCartFromAjaxResponse(ajaxCart),
      detail: { source: BRIDGE_SOURCE }
    });
  } catch (error) {
    dispatchCartError(target, error.message, 'SERVICE_UNAVAILABLE');
    deferred.reject(error);
  }
};

const normalizeAddedLines = (detail) => {
  if (Array.isArray(detail.lines) && detail.lines.length > 0) return detail.lines;

  const responseItems = detail.cartData?.items;
  if (Array.isArray(responseItems) && responseItems.length > 0) {
    return responseItems.map((item) => ({
      merchandiseId: item.variant_id || item.id,
      quantity: Number(item.quantity || 1)
    }));
  }

  return detail.productVariantId ? [{
    merchandiseId: detail.productVariantId,
    quantity: Number(detail.quantity || 1)
  }] : [];
};

const normalizeUpdatedLines = (detail) => {
  if (Array.isArray(detail.lines) && detail.lines.length > 0) return detail.lines;
  if (!detail.lineKey) return [];
  return [{ id: detail.lineKey, quantity: Number(detail.quantity || 0) }];
};

document.addEventListener('afterCartChanged', (legacyEvent) => {
  const detail = legacyEvent.detail || {};
  const target = detail.target || document.querySelector('product-form') || getCartTarget();
  const lines = normalizeAddedLines(detail);
  if (lines.length === 0) return;

  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new CartLinesUpdateEvent({
    action: 'add',
    context: target.closest?.('quick-add-drawer, quick-view-modal') ? 'dialog' : 'product',
    lines,
    promise: deferred.promise
  }));
  resolveCartEvent(deferred, CartLinesUpdateEvent, target);
});

document.addEventListener('afterUpdateQuantity', (legacyEvent) => {
  const detail = legacyEvent.detail || {};
  const target = detail.target || getCartTarget();
  const lines = normalizeUpdatedLines(detail);
  if (lines.length === 0) return;

  const deferred = CartLinesUpdateEvent.createPromise();
  target.dispatchEvent(new CartLinesUpdateEvent({
    action: detail.action || (lines.every((line) => line.quantity === 0) ? 'remove' : 'update'),
    context: 'cart',
    lines,
    promise: deferred.promise
  }));
  resolveCartEvent(deferred, CartLinesUpdateEvent, target);
});

document.addEventListener('afterAddCartError', (legacyEvent) => {
  const detail = legacyEvent.detail || {};
  dispatchCartError(
    detail.target || document.querySelector('product-form') || document,
    detail.errors || detail.message,
    detail.code || 'INVALID'
  );
});

document.addEventListener('afterDiscountUpdate', (legacyEvent) => {
  const detail = legacyEvent.detail || {};
  const target = document.getElementById(detail.id) || getCartTarget();
  const discountCodes = (detail.data?.discount_codes || []).map(({ code }) => ({ code }));
  const deferred = CartDiscountUpdateEvent.createPromise();

  target.dispatchEvent(new CartDiscountUpdateEvent({
    discountCodes,
    promise: deferred.promise
  }));
  resolveCartEvent(deferred, CartDiscountUpdateEvent, target);
});

document.addEventListener('afterVariantChanged', (legacyEvent) => {
  const detail = legacyEvent.detail || {};
  const productInfo = getProductTarget(detail.sectionId);
  const variant = detail.variant;
  const selectionKey = variant?.id || null;
  if (
    selectedVariantByProduct.has(productInfo)
    && selectedVariantByProduct.get(productInfo) === selectionKey
  ) return;
  selectedVariantByProduct.set(productInfo, selectionKey);

  const selectedOptions = getSelectedOptions(productInfo, variant);
  const standardVariant = variant ? {
    id: variant.id,
    title: variant.title,
    availableForSale: variant.available,
    price: normalizeMoney(variant.price),
    selectedOptions
  } : null;

  productInfo.dispatchEvent(new ProductSelectEvent({
    selectedOptions,
    product: {
      id: variant?.product_id || productInfo.dataset.standardProductId || productInfo.dataset.productId,
      title: getProductTitle(productInfo),
      handle: getProductHandle(productInfo)
    },
    promise: Promise.resolve({ variant: standardVariant })
  }));
});

const beginListingUpdate = (searchParams) => {
  const context = window.standardStorefrontContext || {};
  const target = document.getElementById('ProductGridContainer') || document;
  const params = new URLSearchParams(searchParams);

  if (context.pageType === 'search') {
    const deferred = SearchUpdateEvent.createPromise();
    target.dispatchEvent(new SearchUpdateEvent({
      search: {
        query: context.search?.query || params.get('q') || '',
        productFilters: SearchUpdateEvent.parseProductFilters(params)
      },
      promise: deferred.promise
    }));
    return deferred;
  }

  if (context.pageType === 'collection') {
    const deferred = CollectionUpdateEvent.createPromise();
    target.dispatchEvent(new CollectionUpdateEvent({
      collection: {
        id: context.collection?.id || null,
        handle: context.collection?.handle || '',
        productsCount: Number(context.collection?.productsCount || 0)
      },
      productFilters: CollectionUpdateEvent.parseProductFilters(params),
      promise: deferred.promise
    }));
    return deferred;
  }

  return null;
};

const dispatchCartView = async (target, context = 'dialog') => {
  try {
    const ajaxCart = await fetchAjaxCart();
    target.dispatchEvent(new CartViewEvent({
      context,
      cart: createStandardCart(ajaxCart)
    }));
  } catch (error) {
    dispatchCartError(target, error.message, 'SERVICE_UNAVAILABLE');
  }
};

const refreshCartSections = async () => {
  const renderers = [...new Set([
    document.querySelector('cart-drawer'),
    document.querySelector('cart-items')
  ].filter(Boolean))];
  if (renderers.length === 0) return;

  const sectionIds = [...new Set(renderers.flatMap((renderer) => (
    renderer.getSectionsToRender().map((section) => section.id)
  )))];
  const [ajaxCart, sectionResponse] = await Promise.all([
    fetchAjaxCart(),
    fetch(`${window.routes.cart_url}?sections=${sectionIds.join(',')}`).then((response) => {
      if (!response.ok) throw new Error(window.cartStrings?.error || 'Unable to refresh the cart.');
      return response.json();
    })
  ]);

  const state = { ...ajaxCart, sections: sectionResponse };
  renderers.forEach((renderer) => renderer.renderContents(state));
};

const configureActions = () => {
  if (!window.Shopify?.actions) return;

  if (Shopify.actions.openCart?.isDefault()) {
    Shopify.actions.openCart.configure({
      handler() {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer?.open) {
          cartDrawer.open();
          return;
        }
        window.location.assign(window.routes.cart_url);
      }
    });
  }

  if (Shopify.actions.updateCart?.isDefault()) {
    Shopify.actions.updateCart.configure({
      eventTarget: () => getCartTarget(),
      async handler(defaultHandler) {
        const result = await defaultHandler();
        if (!result.userErrors?.length) await refreshCartSections();
        return {
          ...result,
          detail: { ...result.detail, source: ACTION_SOURCE }
        };
      }
    });
  }
};

window.BoosthemeStandardEvents = {
  beginListingUpdate,
  dispatchCartView
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', configureActions, { once: true });
} else {
  configureActions();
}
